/**
 * User-facing copy for AI / scan failures (frontend only).
 */
export function formatAiUserFacingError(rawMessage) {
  const msg = String(rawMessage || "").trim();
  if (!msg) {
    return "The AI service did not return a result. Please try again in a moment.";
  }

  const lower = msg.toLowerCase();
  if (
    lower.includes("high demand") ||
    lower.includes("try again later") ||
    lower.includes("experiencing") ||
    lower.includes("rate limit") ||
    lower.includes("resource exhausted") ||
    lower.includes("overloaded") ||
    lower.includes("503") ||
    lower.includes("429")
  ) {
    return "The AI service is busy right now. Please wait a moment and try again.";
  }

  if (
    lower.includes("did not return") ||
    lower.includes("empty response") ||
    lower.includes("no content")
  ) {
    return "The AI service did not return a result. Please try again in a moment.";
  }

  return msg;
}

/**
 * Formats the official validation attribution string based on admin role and LGU hierarchy.
 */
export function getAdminVerificationBadgeText(user) {
  if (!user) return "Verified & Published by Avisa Admin";
  if (user.role === "system_admin") {
    return "Verified & Published by Avisa Admin";
  }

  const accessLevel = String(user.adminaccessLevel || "").toLowerCase();
  const location = user.location || {};
  const barangay = (location.barangay || "").trim();
  const municipality = (location.municipality || "").trim();
  const province = (location.province || "").trim();

  // Barangay level admin
  if (accessLevel === "barangay" || (barangay && accessLevel !== "municipal" && accessLevel !== "provincial")) {
    const parts = [
      barangay ? `Barangay ${barangay}` : "",
      municipality,
      province,
    ].filter(Boolean).join(", ");
    return `Verified & Published by ${parts ? `${parts} Admin` : "Barangay Admin"}`;
  }

  // Municipal / City level admin
  if (accessLevel === "municipal" || accessLevel === "city" || (municipality && accessLevel !== "provincial")) {
    const parts = [municipality, province].filter(Boolean).join(", ");
    return `Verified & Published by ${parts ? `${parts} Admin` : "Municipal Admin"}`;
  }

  // Provincial level admin
  if (accessLevel === "provincial" || province) {
    return `Verified & Published by ${province ? `${province} Admin` : "Provincial Admin"}`;
  }

  return "Verified & Published by Avisa Admin";
}

/**
 * Required fields before confirming a draft or saving analysis edits.
 */
export function getDocumentAnalysisFormValidation({
  editedTitle,
  editedFilipinoTitle,
  editedOrdinanceNumber,
  editedScopeLevel,
  editedSummary,
  editedFilipinoSummary,
  editedCategory,
  editedKeyPoints = [],
  editedFilipinoKeyPoints = [],
  summaryLanguage = "en",
  editedCitation,
  editedSourceUrl,
}) {
  const isFilipino = summaryLanguage === "fil";
  const universalMessage = "Please complete all required details before confirming.";

  // Title validation
  const effectiveTitle = isFilipino
    ? (editedFilipinoTitle || editedTitle)
    : editedTitle;
  if (!String(effectiveTitle || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Document number validation (stored in the legacy ordinanceNumber field)
  if (!String(editedOrdinanceNumber || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Scope / Level validation
  if (!String(editedScopeLevel || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Category validation
  if (!String(editedCategory || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Citation validation (Required)
  if (editedCitation !== undefined && !String(editedCitation || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Source / Link validation (Required)
  if (editedSourceUrl !== undefined && !String(editedSourceUrl || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Detailed Summary validation
  const effectiveSummary = isFilipino
    ? (editedFilipinoSummary || editedSummary)
    : editedSummary;
  if (!String(effectiveSummary || "").trim()) {
    return { ok: false, message: universalMessage };
  }

  // Key Extracted Points validation
  const effectivePoints = isFilipino
    ? (Array.isArray(editedFilipinoKeyPoints) && editedFilipinoKeyPoints.length > 0
        ? editedFilipinoKeyPoints
        : editedKeyPoints)
    : editedKeyPoints;

  const validPoints = (effectivePoints || []).filter((p) => String(p || "").trim());
  if (!effectivePoints || effectivePoints.length === 0 || validPoints.length === 0) {
    return { ok: false, message: universalMessage };
  }

  const emptyPointIndex = (effectivePoints || []).findIndex(
    (p) => p !== undefined && !String(p || "").trim(),
  );
  if (emptyPointIndex >= 0) {
    return { ok: false, message: universalMessage };
  }

  return { ok: true, message: "" };
}

/**
 * Applies review-form defaults before validating or confirming queued scans.
 */
export function normalizeDraftDocument(doc) {
  const sourceUrl = doc.sourceUrl || doc.source || "";
  const sourceType = doc.sourceType === "online" || /^https?:\/\//.test(sourceUrl)
    ? "online"
    : "office";
  return {
    ...doc,
    legalLevel: doc.legalLevel || (doc.location?.barangay
      ? "Barangay"
      : doc.location?.municipality ? "Municipal" : "National"),
    citation: doc.citation || doc.ordinanceNumber || doc.title || "",
    sourceType,
    sourceUrl: sourceUrl || (sourceType === "office" ? "Office of the Sangguniang Bayan Records" : ""),
    summary: doc.summary || doc.purpose || "",
  };
}

/** Validates a single draft document object for queue-wide validation. */
export function validateDraftDocument(doc) {
  if (!doc) return { ok: false, message: "Invalid document." };
  doc = normalizeDraftDocument(doc);
  const title = doc.title || "";
  const ordinanceNumber = doc.ordinanceNumber || "";
  const legalLevel =
    doc.legalLevel ||
    (doc.location?.barangay
      ? "Barangay"
      : doc.location?.municipality
        ? "Municipal"
        : "");
  const category = doc.category || "";
  const summary = doc.summary || doc.purpose || "";
  const keyPoints = Array.isArray(doc.keyPoints) ? doc.keyPoints : [];
  const citation = doc.citation || "";
  const sourceUrl = doc.sourceUrl || doc.source || "";

  return getDocumentAnalysisFormValidation({
    editedTitle: title,
    editedOrdinanceNumber: ordinanceNumber,
    editedScopeLevel: legalLevel,
    editedCategory: category,
    editedSummary: summary,
    editedKeyPoints: keyPoints,
    editedCitation: citation,
    editedSourceUrl: sourceUrl,
    summaryLanguage: "en",
  });
}

